import { useState, useContext, useEffect, useRef } from 'react';
import { makeStyles } from '@material-ui/styles';
import { FilledInput, Button, FormGroup, FormLabel, FormControl, Box, Grid, Collapse, Divider, TextField } from '@material-ui/core';
import Autocomplete from '@material-ui/lab/Autocomplete';

import AuthContext from '../../context';
import { get, put } from '../../utils/api-services';
import helpers from '../../utils/helpers';
import themeColors from '../../assets/colors';

const useStyles = makeStyles(() => ({
    buttonContainer: {
        marginBottom: 5,
        display: 'flex',
        justifyContent: 'left',
        alignItems: 'center'
    },
    updateBtn: {
        ...themeColors.blueBtn
    }
}));

const defCC2 = { id: 0, name: 'Keep same cost center 2' };
const defCC3 = { id: 0, name: 'Keep same cost center 3' };

const EmployeeHourEdit = ({ open, onClose, hours, permissions }) => {

    const classes = useStyles();
    const { showToast, signOut, showLoader } = useContext(AuthContext);
    const isMounted = useRef(false);
    const [state, setState] = useState({
        cc2: defCC2,
        cc3: defCC3,
        cc2Data: [],
        cc3Data: []
    });

    useEffect(() => {
        if (open && !isMounted.current) {
            makeRequest();
            isMounted.current = true;
        }
    }, [open]);

    const makeRequest = async () => {
        showLoader(true);
        const response = await get('/employeeHour/edit');
        if (response.status === 200) {
            setState(prev => ({ ...prev, cc2Data: response.cc2, cc3Data: response.cc3 }));
        } else {
            console.log('[EmployeeHourEdit] makeRequest error', response);
            helpers.failRequest(response, showToast, signOut);
        }
        showLoader(false);
    }

    const handleAutocomplete = (key, value) => setState(prev => ({ ...prev, [key]: value }));

    const validateForm = () => {
        if (hours?.length < 1) {
            showToast('Select some data to update', 'warning');
            return false;
        }

        if (state.cc2.id === 0 && state.cc3.id === 0) {
            showToast('Select some cost center to update', 'warning');
            return false;
        }

        return window.confirm(`Are you sure you want to update these (${hours?.length}) hours?`);
    }

    const update = async () => {
        const cond = validateForm();
        if (cond) {
            showLoader(true);
            const response = await put('/employeeHour/update', { hours, cc2: state.cc2, cc3: state.cc3 });
            if (response.status === 200) {
                setState(prev => ({ ...prev, cc2: defCC2, cc3: defCC3 }));
                showToast(response.message);
                onClose();
            } else {
                console.log('[EmployeeHourEdit] update error', response);
                helpers.failRequest(response, showToast, signOut);
            }
            showLoader(false);
        }
    }

    return (
        <Collapse in={open}>
            <Divider />

            <h3>{`Edit employee hours`}</h3>

            <Grid container>
                <Grid item xs={12} md={3} lg={2}>
                    <FormGroup>
                        <FormLabel>{'Selected hours'}</FormLabel>
                        <FormControl
                            variant='filled'
                            component={Box}
                            width='100%'
                        >
                            <FilledInput
                                readOnly
                                type='text'
                                placeholder='Selected hours'
                                value={hours?.length ?? '?'}
                            />
                        </FormControl>
                    </FormGroup>
                </Grid>

                <Grid item xs={12} md={6} lg={4}>
                    <FormGroup>
                        <FormLabel>{'Cost center 2'}</FormLabel>
                        <Autocomplete
                            options={state.cc2Data}
                            value={state.cc2}
                            getOptionLabel={value => value?.name ?? ''}
                            getOptionSelected={(option, value) => option.id === value.id}
                            onChange={(event, value) => handleAutocomplete('cc2', value)}
                            renderInput={params => <TextField {...params} placeholder='CC2' />}
                        />
                    </FormGroup>
                </Grid>

                <Grid item xs={12} md={6} lg={4}>
                    <FormGroup>
                        <FormLabel>{'Cost center 3'}</FormLabel>
                        <Autocomplete
                            options={state.cc3Data}
                            value={state.cc3}
                            getOptionLabel={value => value?.name ?? ''}
                            getOptionSelected={(option, value) => option.id === value.id}
                            onChange={(event, value) => handleAutocomplete('cc3', value)}
                            renderInput={params => <TextField {...params} placeholder='CC3' />}
                        />
                    </FormGroup>
                </Grid>

                <Grid item xs={12} md={3} lg={2} className={classes.buttonContainer}>
                    {
                        (permissions.update_attr) &&
                        <Button
                            variant='contained'
                            className={classes.updateBtn}
                            onClick={update}
                        >
                            {'Update'}
                        </Button>
                    }
                </Grid>
            </Grid>

        </Collapse>
    );
}

export default EmployeeHourEdit;